import { useState } from "react";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Copyright from "../Copyright/Copyright";
import CustomizedTreeView from "../CustomizedTreeView/CustomizedTreeView";
import CustomizedDataGrid from "../CustomizedDataGrid/CustomizedDataGrid";
import RevenueChart from "../RevenuChart/RevenueChart";
import StatCard, { StatCardProps } from "../StatCard/StatCard";
import ClientCard from "../ClientCard/ClientCard";
import FilterDropdown from "../FilterDropdown/FilterDropdown";
import UpdateChartsCard from "../UpdateChartsCard/UpdateChartsCard";

import { Client, RevenueChartTypes, DataGridTypes, FilterType } from "../../../utils/types";
import { useQuery } from "@tanstack/react-query";
import { fetchClients } from "../../../utils/helper";

import { useAuth } from "../../../context/AuthContext";

export default function MainGrid() {
  const { jwt } = useAuth();
  
  const [filter, setFilter] = useState<FilterType>({ name: "All", value: "All" });
  const [selectedClient, setSelectedClient] = useState<Client | undefined>();
  
  // Fetch clients from React Query cache
  const { data: clients = [] } = useQuery({
    queryKey: ['clients'],
    queryFn: () => fetchClients(jwt),
  });

  const newClients = clients.reduce((acc, client) => {
    if (client.startDate) {
      const month = new Date(client.startDate).getUTCMonth(); // 0 = Jan, 11 = Dec
      acc[month]++;
    }
    return acc;
  }, new Array(12).fill(0));

  const totalNewClients = newClients.reduce((sum, count) => sum + count, 0);

  const clientData: StatCardProps[] = [
    {
      title: "New Clients",
      value: `${totalNewClients}`,
      interval: "This Year",
      trend: "up",
      data: newClients, // 12 points
    },
    {
      title: "Calls (WIP)",
      value: "325",
      interval: "This Year",
      trend: "up",
      data: [21, 15, 30, 25, 10, 36, 28, 22, 18, 24, 27, 29], // 12 points
    },
  ];

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: { sm: "100%", md: "1700px" },
        height: "100%",
      }}
    >
      {/* cards */}
      <Typography component="h2" variant="h6" sx={{ mb: 2 }}>
        Overview
      </Typography>
      <Grid
        container
        spacing={2}
        columns={12}
        sx={{ mb: (theme) => theme.spacing(2) }}
      >
        <Grid key={"totalClients"} size={{ xs: 6, sm: 6, lg: 4 }}>
          <ClientCard />
        </Grid>
        {clientData.map((card, index) => (
          <Grid key={index} size={{ xs: 6, sm: 6, lg: 4 }}>
            <StatCard {...card} />
          </Grid>
        ))}
        <Grid size={{ xs: 12, md: 12 }}>
          <RevenueChart type={RevenueChartTypes.DASHBOARD} />
        </Grid>
      </Grid>
      <Grid
        container
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Grid>
          <Typography component="h2" variant="h6">
            Clients
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, lg: 4 }}>
          <Box sx={{ maxWidth: 137 }}>
            <FilterDropdown setFilter={setFilter} filter={filter} />
          </Box>
        </Grid>
      </Grid>
      <Grid
        container
        spacing={2}
        columns={12}
        sx={{ flex: 1, minHeight: 0 }}
        alignItems="stretch"
      >
        <Grid size={{ xs: 12, lg: 9 }}>
          <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
            <CustomizedDataGrid page={DataGridTypes.DASHBOARD} filter={filter.value} setSelectedClient={setSelectedClient} selectedClient={selectedClient} />
          </Box>
        </Grid>
        <Grid size={{ xs: 12, lg: 3 }}>
          <Box>
            <UpdateChartsCard />
          </Box>
          <Box>
            <CustomizedTreeView />
          </Box>
        </Grid>
      </Grid>
      <Copyright sx={{ my: 4 }} />
    </Box>
  );
}
